/**
 * switch-installments.js — keeps the custom installments line in sync with the
 * LIVE visible price (AST, tweak 6, 2026-06-23).
 *
 * Why this exists: on the PDP the pack selector is the **Kaching Bundles** app,
 * which rewrites the displayed price WITHOUT firing Horizon's variant:update —
 * so `product-price.js` never re-renders and the Liquid-rendered installments
 * line (`snippets/installments.liquid`) would stay frozen on the base variant
 * price while the shopper sees a bundle total. There is no Horizon variant
 * carrying the bundle price (Kaching multiplies qty on the same variant), so a
 * server re-render isn't reachable — the only place the true price lives is the
 * rendered `.price`. This is the "Logic & integrations = vanilla JS" layer
 * (§4.5): no Alpine, no animation; it just mirrors the parcela arithmetic from
 * the live price. ALL config (count, min, interest-free, copy) is read from the
 * snippet's data-* attrs, so the settings stay single-sourced in Liquid.
 *
 * Robustness: observes the stable <product-price> subtree and re-queries the
 * installments element + the visible price on every change, so it survives the
 * priceContainer being swapped (Horizon variant path) as well as Kaching's
 * in-place price rewrite. Idempotent writes (equality-guarded) prevent the
 * observer from looping on its own mutations.
 *
 * BRL-only (store is Brazil, BRL): money parsing assumes a 2-decimal currency
 * and formatting builds the pt-BR "R$ 1.234,56" shape directly.
 */

(function () {
  // Shopify BRL money always renders 2 decimals → the trailing 2 digits are
  // cents. Strip everything non-numeric and read as an integer cent count.
  function parseCents(text) {
    var digits = (text || '').replace(/\D/g, '');
    return digits ? parseInt(digits, 10) : 0;
  }

  function formatBRL(cents) {
    var reais = Math.floor(cents / 100);
    var rest = String(cents % 100).padStart(2, '0');
    return 'R$ ' + reais.toLocaleString('pt-BR') + ',' + rest;
  }

  // The active price within a <product-price>: the first `.price` that is
  // actually visible (not in the hidden regular/sale branch, not a11y-hidden).
  function visiblePrice(scope) {
    var nodes = scope.querySelectorAll('.price');
    for (var i = 0; i < nodes.length; i++) {
      var p = nodes[i];
      if (p.offsetParent !== null && !p.closest('.price__hidden, .visually-hidden, [hidden]')) {
        return p;
      }
    }
    return null;
  }

  function recompute(scope) {
    var el = scope.querySelector('[data-switch-installments]');
    if (!el) return;
    var priceEl = visiblePrice(scope);
    if (!priceEl) return;

    var cents = parseCents(priceEl.textContent);
    var count = parseInt(el.dataset.count, 10) || 0;
    var minCents = parseInt(el.dataset.minCents, 10) || 0;
    var interestFree = el.dataset.interestFree === 'true';
    var tpl = interestFree ? el.dataset.tplFree : el.dataset.tplInterest;

    var display = 0;
    if (cents > 0 && count > 0) {
      var maxByAmount = minCents > 0 ? Math.floor(cents / minCents) : count;
      display = Math.min(maxByAmount, count);
    }

    if (display >= 2 && tpl) {
      var per = Math.floor(cents / display);
      var text = tpl.replace('__N__', display).replace('__A__', formatBRL(per));
      if (el.textContent !== text) el.textContent = text; // idempotent → no observer loop
      if (el.hasAttribute('hidden')) el.removeAttribute('hidden');
    } else if (!el.hasAttribute('hidden')) {
      el.setAttribute('hidden', '');
    }
  }

  function setup(scope) {
    var raf = null;
    var observer = new MutationObserver(function () {
      if (raf) return; // coalesce bursts (Kaching/Horizon may mutate several nodes)
      raf = requestAnimationFrame(function () {
        raf = null;
        recompute(scope);
      });
    });
    observer.observe(scope, { childList: true, characterData: true, subtree: true });
    recompute(scope); // correct any stale server text vs the current live price
  }

  function init() {
    var seen = [];
    document.querySelectorAll('[data-switch-installments]').forEach(function (el) {
      var scope = el.closest('product-price') || el.closest('[ref="priceContainer"]') || el.parentElement;
      if (scope && seen.indexOf(scope) === -1) {
        seen.push(scope);
        setup(scope);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
